import { lazy, useCallback, useEffect } from 'react';
import { Routes, Route } from 'react-router';
import SignInPage from './pages/sign-in';
import { useAuthStore } from './state/auth.state';
import PaymentsPage from '@/pages/payments';
import SubscribersPage from '@/pages/subscribers';
import { AppLayout } from '@/layouts/app.layout';
import { useUserState } from '@/state/user.state';
import { isUserAdmin } from '@/utils/auth-helpers';
import { logoutEvents } from '@/shared/emmiters';
import { AdminGuard, AppGuard } from '@/shared/guards';
import { useCheckAuth, useLogout } from '@/domain/auth';
import { useSocketBrowserNotifications } from '@/domain/browser-notifications/hooks';


const PageNotFound = lazy(() => import('@/pages/errors/4xx-error'));
const UsersPage = lazy(() => import('@/pages/users'));
const PromoCodesPage = lazy(() => import('@/pages/promo-codes'));
const RefStatisticPage = lazy(() => import('@/pages/ref-statistic'));
const MailingsPage = lazy(() => import('@/pages/mailings'));
const RefLinksPage = lazy(() => import('@/pages/ref-links'));
const TgAuthCheck = lazy(() => import('@/pages/tg-auth'));
const Support = lazy(() => import('@/pages/support'));
const RemnaNetworkMap = lazy(() => import('@/components/remna'));
const BannersPage = lazy(() => import('@/pages/banners.page'));


export function AppRoutes() {
    const role = useUserState((state) => state.role);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    useSocketBrowserNotifications();
    const logout = useLogout();
    
    useEffect(() => {
        return logoutEvents.subscribe(() => {
            if (!isAuthenticated) {
                return;
            }
            logout();
        });
    }, [ isAuthenticated, logout ]);
    
    const { checkAuth } = useCheckAuth();
    
    useEffect(() => {
        checkAuth();
    }, [ checkAuth ])
    
    const isAdmin = isUserAdmin(role);
    
    const onSuccessSignIn = useCallback(() => checkAuth(), [ checkAuth ]);
    
    return (
        <Routes>
            <Route path='tg-auth' element={ <TgAuthCheck/> }/>
            <Route path='sign-in' element={ <SignInPage onSuccessSignIn={ onSuccessSignIn }/> }/>
            
            <Route element={ <AppGuard/> }>
                <Route path='support/:uid?' element={ <Support/> }/>
                
                <Route element={ <AppLayout/> }>
                    <Route
                        index
                        element={ isAdmin ? <RefStatisticPage/> : <UsersPage/> }
                    />
                    <Route path='users' element={ <UsersPage/> }/>
                    <Route path='payments' element={ <PaymentsPage/> }/>
                    
                    <Route element={ <AdminGuard/> }>
                        <Route element={ <RefStatisticPage/> }/>
                        <Route path='promo-codes/*' element={ <PromoCodesPage/> }/>
                        <Route path='mailings/*' element={ <MailingsPage/> }/>
                        <Route path='ref-links/*' element={ <RefLinksPage/> }/>
                        <Route path='remna/*' element={ <RemnaNetworkMap/> }/>
                        <Route path='ref-statistic/:tab?' element={ <RefStatisticPage/> }/>
                        <Route path='subscribers' element={ <SubscribersPage/> }/>
                        <Route path='banners' element={ <BannersPage/> }/>
                    </Route>
                    <Route path='*' element={ <PageNotFound/> }/>
                </Route>
            </Route>
            <Route path='*' element={ <PageNotFound/> }/>
        </Routes>
    )
}